import { FC, useCallback, useContext, useEffect, useState } from "react";
import { v4 as uuid } from "uuid";

import Text from "../../../components/Text";

import Modal from "../../../components/Modal";
import ModalForm from "../../../components/ModalForm";
import { MoneyManagement } from "../../../types/enums/MoneyManagement";
import { SessionType } from "../../../types/enums/SessionType";
import {
    TradingStyle,
    TradingStyleValue,
} from "../../../types/enums/TradingStyle";
import { Timeslots } from "../../../types/enums/Timeslots";
import { Session } from "../../../types/Session";
import { CreateTrade } from "../../../types/Trade";

import { toast } from "react-toastify";
import { AddIcon, HelpIcon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import { AuthContext } from "../../../contexts/AuthContext";
import { getJournalSettings } from "../../../services/api/admin/settings";
import {
    postTradesSession,
    putTradesSession,
} from "../../../services/api/tradingSessions";
import {
    PostTradingSessionInput,
    PutTradingSessionInput,
} from "../../../types/api/tradingSession";
import { isAdminRole } from "../../../utils/graduation";
import {
    getMoneyManagementLabel as formatMoneyManagementLabel,
} from "../../../types/enums/MoneyManagement";
import {
    getFormattedDateInput,
    getFormattedFrDate,
    isWeekendInputDate,
    parseInputDateToLocal,
} from "../../../utils/date";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
    setFieldErrors,
} from "../../../utils/errors";
import { IMAGE_FILE_ACCEPT } from "../../../utils/fileAccept";
import { getOptionsFromEnum } from "../../../utils/form";
import FieldError from "../../FieldError";
import Tooltip from "../../Tooltip";
import styles from "./styles.module.scss";
import TradeList from "./TradeList";

export type TradingSessionFormModalMode = "add" | "edit";

interface Props {
    mode: "add" | "edit";
    open: boolean;
    onClose: () => void;
    /** Appelé après création ou modification réussie d'une session (avant fermeture) */
    onSuccess?: () => void;
    session?: Session | undefined;
    setSession: (session: Session) => void;
    /** En mode « add », préremplit la date (YYYY-MM-DD), ex. clic sur le calendrier */
    initialDateForAdd?: string;
}

const HIGH_GAIN_WARNING_THRESHOLD = 1300;
const DEFAULT_MM_OPTIONS = [150, 300, 500];
const DEFAULT_CHALLENGE_MM = 2500;

const parseTradeResult = (value: CreateTrade["result"] | null): number => {
    if (value === undefined || value === null || value === "") {
        return Number.NaN;
    }
    return typeof value === "number"
        ? value
        : Number(value.replace(",", "."));
};

const TradingSessionFormModal: FC<Props> = ({
    mode,
    open,
    onClose,
    onSuccess,
    session,
    setSession,
    initialDateForAdd,
}) => {
    const { user } = useContext(AuthContext)!;
    const [showConfirmSummaryModal, setShowConfirmSummaryModal] =
        useState(false);
    const [confirmSummaryShowHighGainWarning, setConfirmSummaryShowHighGainWarning] =
        useState(false);

    const dateNow = new Date();
    const adjustedDate = isWeekendInputDate(dateNow)
        ? new Date(
              dateNow.getTime() -
                  (dateNow.getDay() === 0 ? 2 : 1) * 24 * 60 * 60 * 1000
          )
        : dateNow;

    const [files, setFiles] = useState<{ [key: string]: File | null }>({});
    const [date, setDate] = useState(adjustedDate);
    const [lastValidDate, setLastValidDate] = useState(dateNow);
    const [timeslot, setTimeslot] = useState("");
    const [sessionType, setSessionType] = useState("");
    const [tradingStyle, setTradingStyle] = useState("");
    const [moneyManagement, setMoneyManagement] = useState("");
    const [sessionPlan, setSessionPlan] = useState<File | undefined>();
    const [mmOptions, setMmOptions] = useState<number[]>(DEFAULT_MM_OPTIONS);
    const [challengeMm, setChallengeMm] = useState(DEFAULT_CHALLENGE_MM);
    const [trades, setTrades] = useState<CreateTrade[]>([
        {
            id: uuid(),
            coherentPlan: undefined,
            comment: "",
            result: undefined,
            position: 0,
        },
    ]);
    const [fieldErrors, setFieldErrorsState] = useState<FieldErrors>({});

    const allowedMmValues = useCallback(() => {
        return new Set([
            ...mmOptions.map(String),
            String(challengeMm),
        ]);
    }, [mmOptions, challengeMm]);

    useEffect(() => {
        if (!open) return;
        getJournalSettings()
            .then((journal) => {
                if (!journal) return;
                setMmOptions(
                    [...journal.moneyManagementOptions].sort((a, b) => a - b)
                );
                setChallengeMm(journal.challengeMoneyManagement);
            })
            .catch(() => {
                /* keep defaults */
            });
    }, [open]);

    const updateFieldsFromSession = useCallback(() => {
        if (session) {
            setDate(new Date(session.date));
            if (!isWeekendInputDate(session.date)) {
                setLastValidDate(new Date(session.date));
            }
            setTimeslot(session.timeslot);
            setSessionType(session.sessionType || "");
            setTradingStyle(session.tradingStyle || "");
            const allowed = new Set([
                ...mmOptions.map(String),
                String(challengeMm),
            ]);
            setMoneyManagement(
                session.moneyManagement != null &&
                    allowed.has(String(session.moneyManagement))
                    ? String(session.moneyManagement)
                    : session.moneyManagement != null
                      ? String(session.moneyManagement)
                      : ""
            );
            setTrades(
                session.Trade.map((trade, index) => ({
                    id: trade.id,
                    result: trade.result,
                    coherentPlan: trade.coherentPlan,
                    comment: trade.comment || "",
                    position: trade.position !== undefined ? trade.position : index,
                }))
            );
        }
    }, [session, mmOptions, challengeMm]);

    const getMoneyManagementOptions = () => {
        const options: { value: string; label: string }[] = [
            { value: "", label: "-" },
        ];

        mmOptions.forEach((value) => {
            options.push({
                value: String(value),
                label: `${value}€`,
            });
        });

        const sessionKeepsChallenge =
            mode === "edit" &&
            session?.moneyManagement != null &&
            Number(session.moneyManagement) === Number(challengeMm);
        const challengeAlreadyListed = mmOptions.some(
            (value) => Number(value) === Number(challengeMm)
        );
        if (sessionKeepsChallenge && !challengeAlreadyListed) {
            options.push({
                value: String(challengeMm),
                label: "Challenge",
            });
        }

        return options;
    };

    const handleDateChange = (value: Date) => {
        setDate(value);
    };

    const handleDateBlur = () => {
        if (!date) {
            setDate(lastValidDate);
            return;
        }

        if (isWeekendInputDate(date)) {
            toast.warning(
                "Les sessions de trading ne peuvent pas être ajoutées le week-end"
            );
            setDate(lastValidDate);
            return;
        }

        setLastValidDate(date);
    };

    const handleAddTrade = () => {
        const newTrade: CreateTrade = {
            id: uuid(),
            result: undefined,
            coherentPlan: undefined,
            comment: "",
            position: trades.length,
        };

        setTrades([...trades, newTrade]);
    };

    const handleClose = () => {
        onClose();

        if (mode === "edit") {
            updateFieldsFromSession();
            return;
        }

        setFiles({});
        setDate(dateNow);
        setLastValidDate(dateNow);
        setTimeslot("");
        setSessionType("");
        setTradingStyle("");
        setMoneyManagement("");
        setTrades([
            {
                id: uuid(),
                coherentPlan: undefined,
                comment: "",
                result: undefined,
                position: 0,
            },
        ]);
        setFieldErrorsState({});
    };

    const onSubmit = async () => {
        const nextErrors = setFieldErrors(
            ["date", !date && REQUIRED_FIELD],
            ["timeslot", !timeslot && REQUIRED_FIELD],
            ["sessionType", !sessionType && REQUIRED_FIELD],
            ["tradingStyle", !tradingStyle && REQUIRED_FIELD],
            [
                "moneyManagement",
                !allowedMmValues().has(moneyManagement) && REQUIRED_FIELD,
            ],
            ["trades", trades.length === 0 && "Ajoutez au moins un trade."]
        );

        for (const trade of trades) {
            const { result, coherentPlan } = trade;
            if (
                result == null ||
                result === "" ||
                !Number.isFinite(parseTradeResult(result))
            ) {
                nextErrors[`trade.${trade.id}.result`] = REQUIRED_FIELD;
            }
            if (coherentPlan === null || coherentPlan === undefined) {
                nextErrors[`trade.${trade.id}.coherentPlan`] = REQUIRED_FIELD;
            }
        }

        setFieldErrorsState(nextErrors);
        const toastMessage = nextErrors.trades
            ? "Veuillez ajouter au moins un trade."
            : Object.keys(nextErrors).some((key) => key.startsWith("trade."))
              ? "Veuillez remplir le résultat de tous les trades."
              : "Veuillez remplir tous les champs obligatoires.";
        if (
            notifyFieldErrors(nextErrors, {
                message: toastMessage,
                type: "warning",
            })
        ) {
            return;
        }

        if (isWeekendInputDate(date)) {
            toast.warning(
                "Les sessions de trading ne peuvent pas être ajoutées le week-end"
            );
            return;
        }

        const totalGain = trades.reduce((sum, t) => {
            const n = parseTradeResult(t.result);
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
        const isHighGainWarning =
            totalGain > HIGH_GAIN_WARNING_THRESHOLD &&
            moneyManagement !== String(challengeMm);
        setConfirmSummaryShowHighGainWarning(isHighGainWarning);
        setShowConfirmSummaryModal(true);
    };

    const getMoneyManagementLabel = (value: string) => {
        if (!value || !allowedMmValues().has(value)) {
            // Option legacy hors config courante
            if (value) {
                return formatMoneyManagementLabel(value, challengeMm) || "-";
            }
            return "-";
        }
        return formatMoneyManagementLabel(value, challengeMm) || "-";
    };

    const formatRecapNumber = (value: number) =>
        new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(value);

    const doSubmit = async () => {
        if (mode === "add") {
            let input: PostTradingSessionInput = {
                date: getFormattedDateInput(date),
                timeslot: timeslot as Timeslots,
                sessionType: sessionType as SessionType,
                tradingStyle: tradingStyle as TradingStyleValue,
                moneyManagement: Number(moneyManagement) as MoneyManagement,
                trades: trades.map((trade, index) => ({
                    coherentPlan: trade.coherentPlan as boolean,
                    comment: trade.comment ?? null,
                    result: parseTradeResult(trade.result),
                    fileId: files[trades[index].id]
                        ? trades[index].id
                        : trade.fileId,
                    position: trade.position !== undefined ? trade.position : index,
                })),
                files,
            };

            if (sessionPlan) {
                const sessionPlanKey = uuid();
                input.sessionPlanKey = sessionPlanKey;
                input.files = { ...input.files, [sessionPlanKey]: sessionPlan };
            }

            try {
                await postTradesSession(input);
                onSuccess?.();
                handleClose();
                toast.success("Session ajoutée avec succès.");
            } catch (error) {
                return;
            }
        } else if (mode === "edit" && session) {
            let input: PutTradingSessionInput = {
                date: getFormattedDateInput(date),
                timeslot: timeslot as Timeslots,
                sessionType: sessionType as SessionType,
                tradingStyle: tradingStyle as TradingStyleValue,
                moneyManagement: Number(moneyManagement) as MoneyManagement,
                trades: trades.map((trade, index) => ({
                    id: trades[index].id,
                    coherentPlan: trade.coherentPlan as boolean,
                    comment: trade.comment ?? null,
                    result: parseTradeResult(trade.result),
                    fileId: files[trades[index].id]
                        ? trades[index].id
                        : trade.fileId,
                    position: trade.position !== undefined ? trade.position : index,
                })),
                files,
            };

            if (sessionPlan) {
                const sessionPlanKey = uuid();
                input.sessionPlanKey = sessionPlanKey;
                input.files = { ...input.files, [sessionPlanKey]: sessionPlan };
            }

            try {
                const updatedSession = await putTradesSession(
                    session.userId,
                    session.id,
                    input
                );
                if (updatedSession) setSession(updatedSession);
                onSuccess?.();
                handleClose();
                toast.success("Session modifiée avec succès.");
            } catch (error) {
                return;
            }
        }
    };

    const handleAddFile = (tradeId: string, file: File | null) => {
        setFiles((prevFiles) => ({
            ...prevFiles,
            [tradeId]: file,
        }));
    };

    const removeFile = (tradeId: string) => {
        setFiles((prevFiles) => {
            const updatedFiles = { ...prevFiles };
            delete updatedFiles[tradeId];
            return updatedFiles;
        });
    };

    // Si on reçoit une session en props, on met à jour les valeurs des inputs
    useEffect(() => {
        if (session) {
            updateFieldsFromSession();
        }
    }, [session, updateFieldsFromSession]);

    useEffect(() => {
        if (!open || mode !== "add" || !initialDateForAdd || session) return;
        const parsed = parseInputDateToLocal(initialDateForAdd);
        if (!parsed || isWeekendInputDate(parsed)) return;
        setDate(parsed);
        setLastValidDate(parsed);
    }, [open, mode, initialDateForAdd, session]);

    return (
        <>
            <ModalForm
                open={open}
                handleClose={handleClose}
                title={
                mode === "add"
                    ? "Ajouter une session de trading"
                    : "Modifier la session de trading"
            }
            onSubmit={onSubmit}
            size="medium"
            className={styles.modal}
            contentClassName={styles.modalContent}
            fields={[
                {
                    id: "date",
                    label: "Date",
                    type: "date",
                    value: date,
                    onChange: (value) => {
                        handleDateChange(value as Date);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "date")
                        );
                    },
                    onBlur: handleDateBlur,
                    min:
                        !isAdminRole(user?.role)
                            ? new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)
                            : undefined,
                    max: new Date(),
                    error: fieldErrors.date,
                },
                {
                    id: "timeslot",
                    label: "Créneau horaire",
                    type: "select",
                    options: getOptionsFromEnum(Timeslots),
                    value: timeslot,
                    onChange: (value) => {
                        setTimeslot(value as Timeslots);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "timeslot")
                        );
                    },
                    error: fieldErrors.timeslot,
                },
                {
                    id: "sessionType",
                    label: "Type",
                    type: "select",
                    options: getOptionsFromEnum(SessionType),
                    value: sessionType,
                    onChange: (value) => {
                        setSessionType(value as SessionType);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "sessionType")
                        );
                    },
                    error: fieldErrors.sessionType,
                },
                {
                    id: "tradingStyle",
                    label: "Style de trading",
                    type: "select",
                    options: getOptionsFromEnum(TradingStyle),
                    value: tradingStyle,
                    onChange: (value) => {
                        setTradingStyle(value as TradingStyleValue);
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "tradingStyle")
                        );
                    },
                    error: fieldErrors.tradingStyle,
                },
                {
                    id: "moneyManagement",
                    label: "Money management",
                    type: "select",
                    options: getMoneyManagementOptions(),
                    value: String(moneyManagement),
                    onChange: (value) => {
                        setMoneyManagement(String(value));
                        setFieldErrorsState((prev) =>
                            clearFieldError(prev, "moneyManagement")
                        );
                    },
                    error: fieldErrors.moneyManagement,
                },
                {
                    id: "sessionPlan",
                    label: (
                        <Tooltip
                            placement="bottom"
                            content="Télécharger le plan de session global."
                        >
                            <div className={styles.planSessionLabel}>
                                <Text variant="body-bold">Plan de session</Text>
                                <HelpIcon className={styles.helpIcon} />
                            </div>
                        </Tooltip>
                    ),
                    type: "file",
                    accept: IMAGE_FILE_ACCEPT,
                    value: sessionPlan,
                    onChange: (value) =>
                        setSessionPlan(value as File | undefined),
                },
            ]}
            customContent={
                <div className={styles.tradesSection}>
                    <div className={styles.tradesHeader}>
                        <Text variant="subtitle">Trades</Text>
                        {mode === "add" && (
                            <Button
                                size="large"
                                onClick={handleAddTrade}
                                Icon={AddIcon}
                            >
                                Ajouter un trade
                            </Button>
                        )}
                    </div>
                    <FieldError error={fieldErrors.trades} />
                    <TradeList
                        trades={trades}
                        setTrades={(next) => {
                            setTrades(next);
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, "trades")
                            );
                        }}
                        onAddFile={handleAddFile}
                        onRemoveFile={removeFile}
                        mode={mode}
                        fieldErrors={fieldErrors}
                        onClearFieldError={(key) =>
                            setFieldErrorsState((prev) =>
                                clearFieldError(prev, key)
                            )
                        }
                    />
                </div>
            }
        />
            {showConfirmSummaryModal && (
                <div className={styles.highGainWarningModalWrapper}>
                    <Modal
                        open={true}
                        onClose={() => {
                            setShowConfirmSummaryModal(false);
                            setConfirmSummaryShowHighGainWarning(false);
                        }}
                        size="medium"
                        canClose={true}
                        className={styles.confirmSummaryModal}
                    >
                        <div className={styles.confirmSummaryContent}>
                            <Text
                                variant="title"
                                className={styles.confirmSummaryTitle}
                            >
                                Récapitulatif de la session
                            </Text>
                            <Text
                                variant="body"
                                className={styles.confirmSummaryIntro}
                            >
                                Vérifiez attentivement les informations ci-dessous avant de confirmer votre session.
                            </Text>
                            <dl className={styles.confirmSummarySessionRow}>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Date</dt>
                                    <dd>
                                        {date ? getFormattedFrDate(date) : "-"}
                                    </dd>
                                </div>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Créneau</dt>
                                    <dd>
                                        {timeslot
                                            ? Timeslots[
                                                  timeslot as keyof typeof Timeslots
                                              ]
                                            : "-"}
                                    </dd>
                                </div>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Type</dt>
                                    <dd>
                                        {sessionType
                                            ? SessionType[
                                                  sessionType as keyof typeof SessionType
                                              ]
                                            : "-"}
                                    </dd>
                                </div>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Style de trading</dt>
                                    <dd>
                                        {tradingStyle
                                            ? TradingStyle[
                                                  tradingStyle as keyof typeof TradingStyle
                                              ]
                                            : "-"}
                                    </dd>
                                </div>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Money management</dt>
                                    <dd>
                                        {getMoneyManagementLabel(moneyManagement)}
                                    </dd>
                                </div>
                                <div className={styles.confirmSummarySessionItem}>
                                    <dt>Plan de session</dt>
                                    <dd>{sessionPlan ? "Oui" : "Non"}</dd>
                                </div>
                            </dl>
                            <div className={styles.confirmSummaryTableWrapper}>
                                <table className={styles.confirmSummaryTable}>
                                    <thead>
                                        <tr>
                                            <th>Position</th>
                                            <th>Gain / Perte</th>
                                            <th>Méthode respectée</th>
                                            <th>Plan d&apos;entrée</th>
                                            <th>Commentaire</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trades.map((trade, index) => {
                                            const result = trade.result;
                                            const parsedResult =
                                                parseTradeResult(result);
                                            const numResult = Number.isFinite(
                                                parsedResult
                                            )
                                                ? parsedResult
                                                : 0;
                                            const hasEntryPlan = !!files[trade.id];
                                            const gainCellClass =
                                                Number.isFinite(numResult) && numResult > 0
                                                    ? styles.confirmSummaryGainPositive
                                                    : Number.isFinite(numResult) && numResult < 0
                                                    ? styles.confirmSummaryGainNegative
                                                    : undefined;
                                            return (
                                                <tr key={trade.id}>
                                                    <td>Position {index + 1}</td>
                                                    <td className={gainCellClass}>
                                                        {Number.isFinite(numResult)
                                                            ? `${formatRecapNumber(numResult)}$`
                                                            : "-"}
                                                    </td>
                                                <td>
                                                    {trade.coherentPlan === true
                                                        ? "Oui"
                                                        : trade.coherentPlan ===
                                                          false
                                                        ? "Non"
                                                        : "-"}
                                                </td>
                                                <td>
                                                    {hasEntryPlan ? "Oui" : "Non"}
                                                </td>
                                                <td
                                                    className={
                                                        styles.confirmSummaryCommentCell
                                                    }
                                                >
                                                    {trade.comment ? (
                                                        trade.comment.length >
                                                        25 ? (
                                                            <Tooltip
                                                                placement="left"
                                                                content={
                                                                    <div
                                                                        className={
                                                                            styles.confirmSummaryTooltipContent
                                                                        }
                                                                    >
                                                                        {
                                                                            trade.comment
                                                                        }
                                                                    </div>
                                                                }
                                                            >
                                                                <span
                                                                    className={
                                                                        styles.confirmSummaryComment
                                                                    }
                                                                >
                                                                    {`${trade.comment.slice(0, 25)}…`}
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            <span>
                                                                {
                                                                    trade.comment
                                                                }
                                                            </span>
                                                        )
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {(() => {
                                        const total = trades.reduce((sum, t) => {
                                            const n = parseTradeResult(t.result);
                                            return sum + (Number.isFinite(n) ? n : 0);
                                        }, 0);
                                        const totalCellClass =
                                            total > 0
                                                ? styles.confirmSummaryGainPositive
                                                : total < 0
                                                ? styles.confirmSummaryGainNegative
                                                : undefined;
                                        return (
                                            <tr className={styles.confirmSummaryTotalRow}>
                                                <td>Total</td>
                                                <td className={totalCellClass}>
                                                    {formatRecapNumber(total)}$
                                                </td>
                                                <td />
                                                <td />
                                                <td />
                                            </tr>
                                        );
                                    })()}
                                </tbody>
                            </table>
                            </div>
                            {(() => {
                                const total = trades.reduce((sum, t) => {
                                    const n = parseTradeResult(t.result);
                                    return sum + (Number.isFinite(n) ? n : 0);
                                }, 0);
                                return total < HIGH_GAIN_WARNING_THRESHOLD ? (
                                    <div className={styles.confirmSummaryRappelBlock}>
                                        <Text variant="body" className={styles.confirmSummaryRappelText}>
                                            ⚠️ Une fois la session confirmée, il ne sera plus possible d&apos;ajouter de nouveaux trades.
                                        </Text>
                                    </div>
                                ) : null;
                            })()}
                            {confirmSummaryShowHighGainWarning && (
                                <div className={styles.confirmSummaryHighGainBlock}>
                                    <Text
                                        variant="body"
                                        className={styles.confirmSummaryHighGainTitle}
                                    >
                                        Attention
                                    </Text>
                                    <Text
                                        variant="body"
                                        className={styles.confirmSummaryHighGainMessage}
                                    >
                                        Tu t&apos;apprêtes à déclarer un gain
                                        supérieur au maximum autorisé par APEX sur
                                        un compte à 50&nbsp;000$. Tu peux modifier
                                        ta saisie ou confirmer.
                                    </Text>
                                    <Text
                                        variant="caption"
                                        className={styles.confirmSummaryHighGainRappel}
                                    >
                                        Pour rappel, les challenges doivent être
                                        déclarés après avoir sélectionné le risque
                                        «&nbsp;challenge&nbsp;».
                                    </Text>
                                </div>
                            )}
                            <div className={styles.highGainWarningCtas}>
                                <Button
                                    size="newSmall"
                                    variant="secondary"
                                    label="Modifier la session"
                                    onClick={() => {
                                        setShowConfirmSummaryModal(false);
                                        setConfirmSummaryShowHighGainWarning(false);
                                    }}
                                    className={styles.highGainWarningCta}
                                />
                                <Button
                                    size="newSmall"
                                    variant="primary"
                                    label="Confirmer la session"
                                    onClick={() => {
                                        setShowConfirmSummaryModal(false);
                                        setConfirmSummaryShowHighGainWarning(false);
                                        doSubmit();
                                    }}
                                    className={styles.highGainWarningCta}
                                />
                            </div>
                        </div>
                    </Modal>
                </div>
            )}
        </>
    );
};

export default TradingSessionFormModal;
